import React from 'react';

import { Product, ProductFormValues, ProductFormViolation } from 'types/state';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { useServices } from 'hooks/useServices';
import { setCustomTimeout } from 'utils/setCustomTimeout';
import {
  DONE_TIMEOUT_DURATION,
  INPUT_PRODUCT_DEFAULT_PARTNER_COMISSION_MAX_LENGTH,
  INPUT_PRODUCT_NAME_MAX_LENGTH,
} from 'utils/constants';
import { useServiceState } from 'hooks/useServiceState';
import { validateProductFormValues } from 'utils/productFormUtils';
import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { TextComponent } from 'design/text/textComponents';
import { FormControlComponent } from 'design/formControl';
import { FieldsetLayoutComponent } from 'design/fieldsetLayout';
import { MarginComponent } from 'design/margin';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { TextInputControlComponent } from 'design/textInputControl';
import { stringIsEmtyOrDigits } from 'utils/stringUtils';

type TProps = {
  product: Product;
  onCancel(): void;
  onSubmitDidSucceed(): void;
};

export const ProductManagerProductEditor = (props: TProps) => {
  const { product, onCancel, onSubmitDidSucceed } = props;

  const intl = useIntlDictionary();

  const { editProduct: editProductService } = useServices();

  const initialValues = React.useMemo<ProductFormValues>(
    () => ({
      name: product.name,
      defaultAmount: `${product.defaultAmount}`,
      defaultPartnerComission: `${product.defaultPartnerComission}`,
    }),
    [product.defaultAmount, product.defaultPartnerComission, product.name]
  );

  const [values, setValues] = React.useState<ProductFormValues>(initialValues);

  const [violations, setViolations] = React.useState<ProductFormViolation[]>(
    []
  );

  const [editProductState, editProduct] = useServiceState(editProductService);

  React.useEffect(() => {
    if (editProductState && editProductState.resolved) {
      return setCustomTimeout(() => {
        onSubmitDidSucceed();
      }, DONE_TIMEOUT_DURATION);
    }
  }, [values, editProductState, onSubmitDidSucceed]);

  const onSubmit = React.useCallback(() => {
    const violations = validateProductFormValues(values);

    setViolations(violations);

    if (violations.length === 0) {
      editProduct({ product, formValues: values });
    }
  }, [editProduct, product, values]);

  const onNameChange = React.useCallback(
    (nextValue) => {
      setValues((prev) => ({
        ...prev,
        name: nextValue,
      }));
    },
    [setValues]
  );

  const onDefaultPartnerComissionChange = React.useCallback(
    (nextValue) => {
      if (stringIsEmtyOrDigits(nextValue)) {
        setValues((prev) => ({
          ...prev,
          defaultPartnerComission: nextValue,
        }));
      }
    },
    [setValues]
  );

  const hasViolations = violations.length > 0;

  const nameIsInvalid = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'NameMissed':
        default:
          return false;
      }
    }
  }, [violations]);

  const nameHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'NameMissed':
          return intl.inputValidationRequired;
      }
    }
  }, [intl.inputValidationRequired, violations]);

  const defaultPartnerComissionIsInvalid = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'DefaultPartnerCommissionMissed':
        default:
          return false;
      }
    }
  }, [violations]);

  const defaultPartnerComissionHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'DefaultPartnerCommissionMissed':
          return intl.inputValidationRequired;
      }
    }
  }, [intl.inputValidationRequired, violations]);

  return (
    <>
      <SidebarModalSectionComponent
        theme="head"
        content={
          <TextComponent size="mpp" weight="medium">
            {intl.productDetails.productEditorTitle}
          </TextComponent>
        }
        actions={null}
      />
      <FormControlComponent
        shouldFocusFirstInvalidInput={hasViolations}
        onSubmit={onSubmit}
      >
        <SidebarModalSectionComponent
          content={
            <FieldsetLayoutComponent>
              <FloatingLabelComponent
                lifted={!!values.name}
                invalid={nameIsInvalid}
                titleText={
                  <TextComponent color="secondary" size="m">
                    {intl.productNameInputPlaceholder}
                  </TextComponent>
                }
                hintText={
                  <TextComponent color="critical" size="s">
                    {nameHint}
                  </TextComponent>
                }
              >
                <TextInputControlComponent
                  name="name"
                  value={values.name}
                  invalid={nameIsInvalid}
                  onValueChange={onNameChange}
                  maxLength={INPUT_PRODUCT_NAME_MAX_LENGTH}
                />
              </FloatingLabelComponent>
              <FloatingLabelComponent
                lifted={!!values.defaultPartnerComission}
                invalid={defaultPartnerComissionIsInvalid}
                titleText={
                  <TextComponent color="secondary" size="m">
                    {intl.productDefaultPartnerComissionInputPlaceholder}
                  </TextComponent>
                }
                hintText={
                  <TextComponent color="critical" size="s">
                    {defaultPartnerComissionHint}
                  </TextComponent>
                }
              >
                <TextInputControlComponent
                  name="defaultPartnerComission"
                  value={values.defaultPartnerComission}
                  invalid={defaultPartnerComissionIsInvalid}
                  onValueChange={onDefaultPartnerComissionChange}
                  maxLength={INPUT_PRODUCT_DEFAULT_PARTNER_COMISSION_MAX_LENGTH}
                />
              </FloatingLabelComponent>
            </FieldsetLayoutComponent>
          }
          actions={
            <>
              <ModalPressControlComponent
                disabled={editProductState?.pending}
                theme="primary"
                type="submit"
              >
                {intl.saveChangesButton}
              </ModalPressControlComponent>
              <MarginComponent top={20} />
              <ModalPressControlComponent
                disabled={editProductState?.pending}
                theme="secondary"
                type="button"
                onPress={onCancel}
              >
                {intl.cancelButton}
              </ModalPressControlComponent>
            </>
          }
        />
      </FormControlComponent>
    </>
  );
};
